// Condition monitoring: vibration level per component, zones, frequency spectra, 60-day trend, and the chance of failure.
import { rng, hashStr, gauss, clamp, quantile } from './util.js';
import { TURB, COMP } from './content.js';
import { SIM } from './fleet.js';
import { H } from './weather.js';

var REF_LOAD = 0.45, SIGMA = 0.45, SAMPLES = 400;

function loadShare(T, i) { return T.P[i] / TURB.ratedKW; }
// damage growth speed relative to an average day; heavy load speeds it up, a stopped turbine almost freezes it
function growthFactor(L) { return (0.25 + 0.75 * L * L) / (0.25 + 0.75 * REF_LOAD * REF_LOAD); }

// severity of the issue at hour i (expected path with the forecast load)
export function severityPath(t, loadArr) {
  var is = t.issue, s = is.severity, out = new Float32Array(loadArr.length + 1); out[0] = s;
  for (var i = 0; i < loadArr.length; i++) { if (is.f.grows) s = Math.min(1.2, s * Math.exp(is.growthPerDay / 24 * growthFactor(loadArr[i]))); out[i + 1] = s; }
  return out;
}

// vibration value of each component at hour i
export function metrics(t, i) {
  var T = SIM.tur[t.id], L = loadShare(T, i), r = rng(hashStr(t.id + 'm')), res = {}, is = t.issue;
  var s = is ? severityPath(t, loadsOf(T, i)).slice(-1)[0] : 0;
  TURB.components.forEach(function (c) {
    var base = c.base * (0.85 + 0.3 * r()), val;
    if (c.unit === '°') val = base;
    else val = base * (0.35 + 0.65 * L) * (T.P[i] > 0 || T.code[i] === 'idle' ? 1 : 0.15);
    if (is && is.f.component === c.id) {
      if (c.unit === '°') val = is.f.deg ? is.f.deg * is.severity / 0.6 : val;
      else val += is.f.gain * Math.pow(s, 1.6) * (0.3 + 0.7 * L) * (T.P[i] > 0 ? 1 : 0.1);
    }
    if (is && is.f.id === 'pitch_offset' && c.id === 'rotor') val += 0.32 * (0.3 + 0.7 * L);
    if (is && is.f.id === 'mass_imbalance' && c.id === 'mainbearing') val += 0.5 * s * L;
    if (is && is.f.id === 'gen_misalignment' && c.id === 'gearbox') val += 0.6 * s * L;
    res[c.id] = { value: val, zone: zone(c, val) };
  });
  return res;
}
function loadsOf(T, upto) { var a = []; for (var i = 0; i < upto; i++) a.push(loadShare(T, i)); return a; }

export function zone(c, v) { return v >= c.limits[1] ? 2 : v >= c.limits[0] ? 1 : 0; }
export function worstZone(m) { var z = 0; Object.keys(m).forEach(function (k) { z = Math.max(z, m[k].zone); }); return z; }

// ---------- chance of failure ----------
// 400 simulated futures: growth rate and today's damage level are uncertain; load follows the forecast.
export function failureOdds(t, opts) {
  var is = t.issue; if (!is || !is.f.grows) return null;
  var T = SIM.tur[t.id], r = rng(hashStr(t.id + 'mc')), times = [], cap = opts && opts.derate ? 0.7 : 1, stopAt = opts && opts.stopFrom != null ? opts.stopFrom : 1e9;
  var loads = []; for (var i = 0; i < H; i++) loads.push(i >= stopAt ? 0 : Math.min(cap, loadShare(T, i) || 0));
  for (var k = 0; k < SAMPLES; k++) {
    var lam = is.growthPerDay * Math.exp(SIGMA * gauss(r)), s = is.severity * Math.exp(0.07 * gauss(r)), tf = Infinity;
    for (var h = 0; h < 24 * 120; h++) {
      var L = h < H ? loads[h] : (h >= stopAt ? 0 : Math.min(cap, REF_LOAD));
      s *= Math.exp(lam / 24 * growthFactor(L));
      if (s >= 1) { tf = h + 1; break; }
    }
    times.push(tf);
  }
  times.sort(function (a, b) { return a - b; });
  function pBefore(hh) { var c = 0; for (var j = 0; j < times.length; j++) if (times[j] <= hh) c++; else break; return c / times.length; }
  return { p72: pBefore(72), p24: pBefore(24), p30d: pBefore(720), p90d: pBefore(24 * 90), pBefore: pBefore, median: quantile(times.filter(isFinite), 0.5), q10: quantile(times, 0.1), q90: quantile(times, 0.9), times: times };
}

// ---------- 60-day trend of the component that carries the issue ----------
export function trend(t) {
  var is = t.issue, comp = is ? COMP[is.f.component] : COMP.gearbox, r = rng(hashStr(t.id + 'tr')), days = [];
  for (var d = -60; d <= 0; d++) {
    // condition monitoring compares like with like: daily values in the 40–60 % power bin
    var L = clamp(0.5 + 0.04 * gauss(r), 0.4, 0.6), s = is && is.f.grows ? is.severity * Math.exp(is.growthPerDay * d) : is ? is.severity : 0, v;
    if (comp.unit === '°') v = is && is.f.deg ? is.f.deg * is.severity / 0.6 * (d < -38 ? 0.15 : 1) + 0.15 * gauss(r) : comp.base;
    else { v = comp.base * (0.35 + 0.65 * L) * (1 + 0.06 * gauss(r)); if (is && is.f.component === comp.id) v += is.f.gain * Math.pow(Math.max(0, s), 1.6) * (0.3 + 0.7 * L) * (is.f.grows ? 1 : (d < -38 ? 0.2 : 1)); }
    days.push({ d: d, v: Math.max(0, v), L: L });
  }
  return { comp: comp, days: days };
}

// ---------- frequency spectrum ----------
// Kinematic frequencies from rotor speed; amplitudes grow with the fault that is present.
export function spectrum(t, i) {
  var T = SIM.tur[t.id], rpm = Math.max(T.rpm[i], 0.6), fr = rpm / 60, dt = TURB.drivetrain, fg = fr * dt.gearRatio, fims = fr * dt.gearRatio / dt.imsRatio, L = loadShare(T, i);
  var is = t.issue, s = is ? (is.f.grows ? severityPath(t, loadsOf(T, i)).slice(-1)[0] : is.severity) : 0, sig = is ? is.f.signature : [], amp = function (id, a0, k) { return a0 * (0.4 + 0.6 * L) + (sig.indexOf(id) >= 0 ? k * Math.pow(s, 1.4) * (0.3 + 0.7 * L) : 0); };
  var low = [
    { id: 'p1', f: fr, a: amp('p1', 0.02, is && is.f.id === 'pitch_offset' ? 0.22 : 0.3), label: '1P rotor' },
    { id: 'tower', f: TURB.towerHz, a: amp('tower', 0.03, 0.18), label: 'tower 1st mode' },
    { id: 'p3', f: 3 * fr, a: amp('p3', 0.025, 0.12), label: '3P blade pass' },
    { id: 'bpfo_main', f: dt.mainBearingBPFO * fr, a: amp('bpfo_main', 0.004, 0.08), label: 'main bearing BPFO' },
    { id: 'bpfo_main2', f: 2 * dt.mainBearingBPFO * fr, a: amp('bpfo_main2', 0.002, 0.04), label: '2 × main bearing BPFO' }
  ];
  var high = [
    { id: 'ims', f: fims, a: amp('ims', 0.25, 0), label: 'IMS shaft' },
    { id: 'g1', f: fg, a: amp('g1', 0.6, 1.2), label: '1 × generator' },
    { id: 'g2', f: 2 * fg, a: amp('g2', 0.25, 2.6), label: '2 × generator' },
    { id: 'g3', f: 3 * fg, a: amp('g3', 0.1, 0.8), label: '3 × generator' },
    { id: 'bpfo_gen', f: dt.genBearingBPFO * fg, a: amp('bpfo_gen', 0.05, 1.6), label: 'generator bearing BPFO' },
    { id: 'bpfo_gen2', f: 2 * dt.genBearingBPFO * fg, a: amp('bpfo_gen2', 0.03, 0.8), label: '2 × generator bearing BPFO' },
    { id: 'bpfo_hss', f: dt.hssBearingBPFO * fg, a: amp('bpfo_hss', 0.06, 2.4), label: 'HSS bearing BPFO' },
    { id: 'bpfo_hss2', f: 2 * dt.hssBearingBPFO * fg, a: amp('bpfo_hss2', 0.03, 1.4), label: '2 × HSS bearing BPFO' },
    { id: 'bpfo_hss3', f: 3 * dt.hssBearingBPFO * fg, a: amp('bpfo_hss3', 0.02, 0.8), label: '3 × HSS bearing BPFO' },
    { id: 'gmf', f: dt.hssPinionTeeth * fg, a: amp('gmf', 0.9, 0), label: 'HSS gear mesh' }
  ];
  if (sig.indexOf('sb_hss') >= 0) [-1, 1].forEach(function (k) { high.push({ id: 'sb', f: dt.hssBearingBPFO * fg + k * fg, a: 0.9 * Math.pow(s, 1.4) * (0.3 + 0.7 * L), label: 'side band ±1 × shaft' }); });
  [-1, 1].forEach(function (k) { high.push({ id: 'gsb', f: dt.hssPinionTeeth * fg + k * fg, a: 0.12 * (0.4 + 0.6 * L), label: 'gear mesh side band' }); });
  return { rpm: rpm, fr: fr, fg: fg, low: low, high: high, noise: 0.012 + 0.01 * L, s: s };
}
